import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE, getToken} from '../lib/api';
import { 
  Hash, Play, Copy, Check, RefreshCw, Settings, BarChart3, 
  Shield, Clock, Layers, ChevronDown, ChevronRight, AlertCircle,
  Zap, Database, Code, TestTube, CheckCircle2, XCircle
} from 'lucide-react';


// API helper
const api = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  return response.json();
};

const ULIDGeneratorScreen = ({ theme = 'light', onClose }) => {
  const isDark = theme === 'dark';
  
  // State
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Generation state
  const [components, setComponents] = useState({
    appId: '1',
    companyId: '1',
    countryId: '1',
    branchId: '1',
    moduleId: '1',
    screenId: '1',
    userId: '1'
  });
  const [generatedId, setGeneratedId] = useState(null);
  const [recentIds, setRecentIds] = useState([]);
  const [copied, setCopied] = useState(false);
  
  // Decode state
  const [decodeInput, setDecodeInput] = useState('');
  const [decodedResult, setDecodedResult] = useState(null);
  
  // Stats state
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Benchmark state
  const [benchmarkResults, setBenchmarkResults] = useState(null);
  const [benchmarkIterations, setBenchmarkIterations] = useState(1000);
  
  // Verification state
  const [verificationResult, setVerificationResult] = useState(null);

  // Load initial data
  useEffect(() => {
    loadStats();
    loadConfig();
    loadRecent();
  }, []);

  const loadStats = async () => {
    try {
      const result = await api('/api/ulid/stats');
      if (result.success) setStats(result.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const loadConfig = async () => {
    try {
      const result = await api('/api/ulid/config');
      if (result.success) setConfig(result.data);
    } catch (e) {
      console.error('Failed to load config');
    }
  };

  const loadRecent = async () => {
    try {
      const result = await api('/api/ulid/recent?limit=20');
      if (result.success) setRecentIds(result.data.records || []);
    } catch (e) {
      console.error('Failed to load recent');
    }
  };

  // Generate ULID
  const generateULID = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api('/api/ulid/generate', {
        method: 'POST',
        body: JSON.stringify(components)
      });
      if (result.success) {
        setGeneratedId(result.data);
        setSuccess('ULID generated successfully!');
        loadRecent();
        loadStats();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Generation failed');
      }
    } catch (e) {
      setError('Failed to generate ULID');
    }
    setLoading(false);
  };

  // Generate batch
  const generateBatch = async (count) => {
    setLoading(true);
    setError('');
    try {
      const result = await api('/api/ulid/generate-batch', {
        method: 'POST',
        body: JSON.stringify({ ...components, count })
      });
      if (result.success) {
        setSuccess(`Generated ${result.data.count} ULIDs in ${result.data.duration}`);
        loadRecent();
        loadStats();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (e) {
      setError('Batch generation failed');
    }
    setLoading(false);
  };

  // Decode ULID
  const decodeULID = async () => {
    if (!decodeInput.trim()) return;
    setLoading(true);
    setError('');
    try {
      const result = await api(`/api/ulid/decode/${decodeInput.trim()}`);
      if (result.success) {
        setDecodedResult(result.data);
      } else {
        setError(result.message || 'Decode failed');
        setDecodedResult(null);
      }
    } catch (e) {
      setError('Failed to decode ULID');
      setDecodedResult(null);
    }
    setLoading(false);
  };

  // Run benchmark
  const runBenchmark = async () => {
    setLoading(true);
    setError('');
    setBenchmarkResults(null);
    try {
      const result = await api('/api/ulid/benchmark', {
        method: 'POST',
        body: JSON.stringify({ iterations: benchmarkIterations })
      });
      if (result.success) {
        setBenchmarkResults(result.data);
      } else {
        setError(result.message || 'Benchmark failed');
      }
    } catch (e) {
      setError('Benchmark failed');
    }
    setLoading(false);
  };

  // Verify integrity
  const verifyIntegrity = async () => {
    setLoading(true);
    try {
      const result = await api('/api/ulid/verify');
      if (result.success) {
        setVerificationResult(result.data);
      }
    } catch (e) {
      setError('Verification failed');
    }
    setLoading(false);
  };

  // Copy to clipboard — safe-wrapped to survive sandbox permission-policy blocks
  const copyToClipboard = async (text) => {
    const { copyToClipboard: safeCopy } = await import('@/utils/clipboard');
    const ok = await safeCopy(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.prompt('Copy the value below (Ctrl+C):', String(text || ''));
    }
  };

  // Styles
  const cardClass = `rounded-xl border ${isDark ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white border-slate-200'} p-4`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0a0a0a] border-[#333] text-white' : 'bg-slate-50 border-slate-200 text-slate-900'} focus:outline-none focus:border-blue-500`;
  const labelClass = `text-[10px] font-bold uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  const btnPrimary = `px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50`;
  const btnSecondary = `px-4 py-2 ${isDark ? 'bg-[#222] hover:bg-[#333] text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} rounded-lg text-sm font-medium transition-all`;

  const tabs = [
    { id: 'generate', label: 'Generate', icon: <Zap size={16} /> },
    { id: 'decode', label: 'Decode', icon: <Code size={16} /> },
    { id: 'benchmark', label: 'Benchmark', icon: <BarChart3 size={16} /> },
    { id: 'verify', label: 'Verify', icon: <Shield size={16} /> },
    { id: 'config', label: 'Config', icon: <Settings size={16} /> }
  ];

  return (
    <div data-testid="ulid-generator-screen" className={`h-full overflow-auto p-4 lg:p-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-600' : 'bg-blue-600'} text-white`}>
            <Hash size={20} />
          </div>
          <div>
            <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Enterprise ULID Generator</h1>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>High-performance time-ordered ID generation</p>
          </div>
        </div>
        
        {/* Quick Stats */}
        {stats && (
          <div className="hidden md:flex items-center gap-4">
            <div className={`px-3 py-1.5 rounded-lg ${isDark ? 'bg-[#151515]' : 'bg-white'}`}>
              <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Generated</span>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.totalGenerated?.toLocaleString()}</p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg ${stats.hashChainValid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              <span className={`text-[10px] ${stats.hashChainValid ? 'text-green-500' : 'text-red-500'}`}>Chain Status</span>
              <p className={`text-sm font-bold ${stats.hashChainValid ? 'text-green-500' : 'text-red-500'}`}>
                {stats.hashChainValid ? 'Valid' : 'Invalid'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl mb-6 ${isDark ? 'bg-[#151515]' : 'bg-slate-100'}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-blue-600 text-white' 
                : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-sm text-red-500">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-green-500" />
          <span className="text-sm text-green-500">{success}</span>
        </div>
      )}

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>ID Components</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(components).map(([key, value]) => (
                <div key={key}>
                  <label className={labelClass}>{key.replace('Id', '')}</label>
                  <input
                    data-testid={`input-${key}`}
                    type="text"
                    value={value}
                    onChange={(e) => setComponents(prev => ({ ...prev, [key]: e.target.value }))}
                    className={inputClass}
                    maxLength={2}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button 
                data-testid="btn-generate"
                onClick={generateULID} 
                disabled={loading}
                className={btnPrimary}
              >
                {loading ? 'Generating...' : 'Generate ULID'}
              </button>
              <button onClick={() => generateBatch(10)} disabled={loading} className={btnSecondary}>
                Batch (10)
              </button>
              <button onClick={() => generateBatch(100)} disabled={loading} className={btnSecondary}>
                Batch (100)
              </button>
            </div>
          </div>

          {/* Generated Result */}
          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Generated ULID</h3>
            {generatedId ? (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'} flex items-center justify-between`}>
                  <code className={`text-xl font-mono font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    {generatedId.ulid}
                  </code>
                  <button
                    onClick={() => copyToClipboard(generatedId.ulid)}
                    className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#222]' : 'hover:bg-slate-200'}`}
                  >
                    {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} className={isDark ? 'text-slate-400' : 'text-slate-500'} />}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Timestamp:</span> <span className={isDark ? 'text-white' : 'text-slate-900'}>{generatedId.timestamp}</span></div>
                  <div><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Sequence:</span> <span className={isDark ? 'text-white' : 'text-slate-900'}>{generatedId.sequence}</span></div>
                  <div className="col-span-2"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Created:</span> <span className={isDark ? 'text-white' : 'text-slate-900'}>{new Date(generatedId.createdAt).toLocaleString()}</span></div>
                  <div className="col-span-2 break-all"><span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Hash:</span> <code className="text-[10px] font-mono text-blue-500">{generatedId.hash?.slice(0, 32)}...</code></div>
                </div>
              </div>
            ) : (
              <div className={`p-8 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'} text-center`}>
                <Hash size={40} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Generate a ULID to see result</p>
              </div>
            )}
          </div>

          {/* Recent IDs */}
          <div className={`${cardClass} lg:col-span-2`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Recent Generated IDs</h3>
              <button onClick={loadRecent} className={`p-2 rounded-lg ${isDark ? 'hover:bg-[#222]' : 'hover:bg-slate-100'}`}>
                <RefreshCw size={16} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                    <th className="text-left py-2 px-2">ULID</th>
                    <th className="text-left py-2 px-2">App</th>
                    <th className="text-left py-2 px-2">Module</th>
                    <th className="text-left py-2 px-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {recentIds.slice(0, 10).map((record, idx) => (
                    <tr key={idx} className={`border-t ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                      <td className="py-2 px-2">
                        <code className={`font-mono ${isDark ? 'text-green-400' : 'text-green-600'}`}>{record.ulid}</code>
                      </td>
                      <td className={`py-2 px-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{record.appId}</td>
                      <td className={`py-2 px-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{record.moduleId}</td>
                      <td className={`py-2 px-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {new Date(record.createdAt).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {recentIds.length === 0 && (
                <p className={`text-center py-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No IDs generated yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Decode Tab */}
      {activeTab === 'decode' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Decode ULID (No DB Lookup)</h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Enter ULID</label>
                <input
                  data-testid="decode-input"
                  type="text"
                  value={decodeInput}
                  onChange={(e) => setDecodeInput(e.target.value.toUpperCase())}
                  placeholder="Enter 16-character ULID"
                  className={inputClass}
                  maxLength={20}
                />
              </div>
              <button 
                data-testid="btn-decode"
                onClick={decodeULID} 
                disabled={loading || !decodeInput.trim()}
                className={btnPrimary}
              >
                {loading ? 'Decoding...' : 'Decode'}
              </button>
            </div>
          </div>

          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Decoded Components</h3>
            {decodedResult ? (
              <div className="space-y-2">
                {Object.entries(decodedResult.decoded || {}).map(([key, value]) => (
                  <div key={key} className={`flex justify-between py-2 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{key}</span>
                    <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {key === 'createdAt' ? new Date(value).toLocaleString() : String(value)}
                    </span>
                  </div>
                ))}
                <p className="text-xs text-blue-500 mt-4 flex items-center gap-1">
                  <CheckCircle2 size={12} />
                  Decoded without database lookup
                </p>
              </div>
            ) : (
              <div className={`p-8 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'} text-center`}>
                <Code size={40} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Enter a ULID to decode</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Benchmark Tab */}
      {activeTab === 'benchmark' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Performance Benchmark</h3>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Iterations</label>
                <input
                  type="number"
                  value={benchmarkIterations}
                  onChange={(e) => setBenchmarkIterations(Math.min(100000, Math.max(100, parseInt(e.target.value) || 1000)))}
                  className={inputClass}
                  min={100}
                  max={100000}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Max: 100,000 iterations</p>
              </div>
              <button 
                data-testid="btn-benchmark"
                onClick={runBenchmark} 
                disabled={loading}
                className={btnPrimary}
              >
                {loading ? 'Running...' : 'Run Benchmark'}
              </button>
            </div>
          </div>

          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Results</h3>
            {benchmarkResults ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Throughput</p>
                    <p className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {benchmarkResults.throughput?.perSecond?.toLocaleString()}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>IDs/second</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Duration</p>
                    <p className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {benchmarkResults.durationMs}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>milliseconds</p>
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${benchmarkResults.collisions === 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  <div className="flex items-center gap-2">
                    {benchmarkResults.collisions === 0 ? (
                      <CheckCircle2 size={20} className="text-green-500" />
                    ) : (
                      <XCircle size={20} className="text-red-500" />
                    )}
                    <div>
                      <p className={`font-bold ${benchmarkResults.collisions === 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {benchmarkResults.collisions === 0 ? 'No Collisions!' : `${benchmarkResults.collisions} Collisions Detected`}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {benchmarkResults.uniqueIds?.toLocaleString()} unique IDs generated
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <p className={`text-xs mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Sample IDs:</p>
                  <div className="space-y-1">
                    {benchmarkResults.sampleIds?.slice(0, 5).map((id, idx) => (
                      <code key={idx} className={`text-xs font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{id}</code>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`p-8 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'} text-center`}>
                <BarChart3 size={40} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Run benchmark to see results</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verify Tab */}
      {activeTab === 'verify' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Hash Chain Verification</h3>
            <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Verify blockchain-like tamper detection by checking hash chain integrity.
            </p>
            <button 
              data-testid="btn-verify"
              onClick={verifyIntegrity} 
              disabled={loading}
              className={btnPrimary}
            >
              {loading ? 'Verifying...' : 'Verify Hash Chain'}
            </button>
          </div>

          <div className={cardClass}>
            <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Verification Result</h3>
            {verificationResult ? (
              <div className={`p-6 rounded-lg ${verificationResult.valid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <div className="flex items-center gap-3 mb-4">
                  {verificationResult.valid ? (
                    <CheckCircle2 size={32} className="text-green-500" />
                  ) : (
                    <XCircle size={32} className="text-red-500" />
                  )}
                  <div>
                    <p className={`text-xl font-bold ${verificationResult.valid ? 'text-green-500' : 'text-red-500'}`}>
                      {verificationResult.valid ? 'Chain Valid' : 'Chain Compromised'}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {verificationResult.details}
                    </p>
                  </div>
                </div>
                {verificationResult.brokenAt && (
                  <p className="text-sm text-red-500">
                    Broken at ULID: <code className="font-mono">{verificationResult.brokenAt}</code>
                  </p>
                )}
              </div>
            ) : (
              <div className={`p-8 rounded-lg ${isDark ? 'bg-[#0a0a0a]' : 'bg-slate-50'} text-center`}>
                <Shield size={40} className={`mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Click verify to check integrity</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div className={cardClass}>
          <h3 className={`font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Current Configuration</h3>
          {config ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`text-sm font-bold mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>General Settings</h4>
                <div className="space-y-2">
                  <div className={`flex justify-between py-2 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>ID Length</span>
                    <span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{config.idLength}</span>
                  </div>
                  <div className={`flex justify-between py-2 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Mode</span>
                    <span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{config.mode}</span>
                  </div>
                  <div className={`flex justify-between py-2 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Custom Epoch</span>
                    <span className={`font-mono text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(config.customEpoch).toISOString().split('T')[0]}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className={`text-sm font-bold mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Field Lengths</h4>
                <div className="space-y-2">
                  {config.fieldLengths && Object.entries(config.fieldLengths).map(([key, value]) => (
                    <div key={key} className={`flex justify-between py-2 border-b ${isDark ? 'border-[#222]' : 'border-slate-100'}`}>
                      <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{key}</span>
                      <span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className={isDark ? 'text-slate-500' : 'text-slate-400'}>Loading configuration...</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ULIDGeneratorScreen;
